import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { UserService } from './user.service';
import { Claim } from '../models/Claim';
import { ClaimWriteModel } from '../models/Claim';
import { Role } from '../models/Role';
import { RoleWriteModel } from '../models/Role';
import { Location } from '../models/Location';
import {environment} from '../../environments/environment';
import {AuthService} from './auth.service';
import {HttpHeaders, HttpParams, HttpResponse} from '@angular/common/http';
import {flatMap, mapTo, mergeMap, tap} from 'rxjs/operators';
import {HttpService} from './http.service';
import {LocationsService} from "./locations.service";
import {Pagination} from "../models/Pagination";

@Injectable()
export class ClaimsService {


    constructor(
        private userService: UserService,
        private httpService: HttpService,
        private authService: AuthService,
        private locationsService: LocationsService
    ) { }

    // User Claim Methods
    getAllClaimsForUser(userId: number): Observable<Array<Claim>> {
        return this.httpService.get<Array<Claim>>(
            environment.apiAccountV3BaseUrl + '/users/' + userId + '/claims',
            { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) }
        );
    }

    addClaimToUser(claim: ClaimWriteModel, userId: number) {
        return this.httpService.post(environment.apiAccountV3BaseUrl + '/users/' + userId + '/claims', claim, { observe: 'response'}).pipe(
            tap((response: HttpResponse<any>) => {
                if (response.status !== 201) {
                    console.warn('Add claim failed with status: ' + response.status + ' (Expected 201)');
                }

                if (userId === this.userService.currentUser.id) {

                    // Auto-refresh own token because claims have changed for user
                    this.authService.startTokenRefreshTimer(true);
                }
            })
        );
    }

    removeClaimFromUser(userId: number, claimId: number) {
        return this.httpService.delete(environment.apiAccountV3BaseUrl + '/users/' + userId + '/claims/' + claimId).pipe(
            tap(() => {
                if (userId === this.userService.currentUser.id) {
                    // Auto-refresh own token because claims have changed for user
                    this.authService.startTokenRefreshTimer(true);
                }
            }),
            mergeMap(() => this.getAllClaimsForUser(userId))
        );
    }

    getLocations(): Observable<Pagination> {
        return this.locationsService.getLocationsPaginated(50, 0, null);
    }

    // Role Claim Methods
    getAllRoles(): Observable<Array<Role>> {
        return this.httpService.get<Array<Role>>(environment.apiAccountV3BaseUrl + '/roles/');
    }

    getAllUserRoles(userId: number): Observable<Array<Role>> {

        let params = new HttpParams();
        params = params.set('userId', userId.toString());

        return this.httpService.get<Array<Role>>(environment.apiAccountV3BaseUrl + '/roles', { params: params });
    }

    getClaimsForRole(roleId: number): Observable<Array<Claim>> {
        return this.httpService.get<Array<Claim>>(environment.apiAccountV3BaseUrl + '/roles/' + roleId + '/claims');
    }

    removeRoleFromUser(userId: number, roleId: number) {
        return this.httpService.delete(environment.apiAccountV3BaseUrl + '/roles/' + roleId + '/members/' + userId).pipe(
            tap(() => {
                if (userId === this.userService.currentUser.id) {
                    // Auto-refresh own token because claims have changed for user
                    this.authService.startTokenRefreshTimer(true);
                }
            })
        );
    }

    // Error message fucked, look into
    addRoleToUser(role: RoleWriteModel, userId: number) {
        return this.httpService.put(environment.apiAccountV3BaseUrl + '/roles/' + role.id + '/members/' + userId, {}, { observe: 'response' }).pipe(
            tap((response: HttpResponse<any>) => {
                if (response.status !== 200) {
                    throw new Error('Add claim failed with status: ' + response.status + ' (Expected 201)');
                }

                if (userId === this.userService.currentUser.id) {
                    // Auto-refresh own token because claims have changed for user
                    this.authService.startTokenRefreshTimer(true);
                }
            }),
            mergeMap(() => this.getRoleMembers(role.id))
        );
    }

    removeClaimFromRole(claimId: number, roleId: number): Observable<boolean> {
        return this.httpService.delete(
            environment.apiAccountV3BaseUrl + '/roles/' + roleId + '/claims/' + claimId,
            { observe: 'response' }
        ).pipe(
            mapTo(true)
        );
    }

    getSingleRole(roleId: number): Observable<Role> {
        return this.httpService.get<Role>(environment.apiAccountV3BaseUrl + '/roles/' + roleId);
    }

    addClaimToRole(claim: ClaimWriteModel, roleId: number) {
        return this.httpService.post(environment.apiAccountV3BaseUrl + '/roles/' + roleId + '/claims/', claim);
    }

    createRole(roleName: string) {
        return this.httpService.post(environment.apiAccountV3BaseUrl + '/roles', { name: roleName });
    }

    deleteRole(roleId: number) {
        return this.httpService.delete(environment.apiAccountV3BaseUrl + '/roles/' + roleId);
    }

    getRoleMembers(roleId: number) {
        return this.httpService.get<Array<number>>(environment.apiAccountV3BaseUrl + '/roles/' + roleId + '/members');
    }
}
